/**
 * Shared helpers for turning a failed API call into what the UI needs:
 * a single human-readable message for a toast, and a field -> message map
 * for inline form errors.
 *
 * The backend's validation (422) responses look like:
 *   { status: false, message: "Validation failed", errors: { email: ["The email has already been taken."] } }
 *
 * Two different shapes can reach these helpers depending on the call site:
 *  - a raw axios error, where the body above lives at `error.response.data`
 *  - a Redux Toolkit `rejectWithValue(...)` payload (from `.unwrap()`'s
 *    catch), which callers in this app pass the same `{ message, errors }`
 *    shape into directly, so it shows up as `error` itself.
 */

export interface ApiErrorPayload {
  message?: string;
  errors?: Record<string, string[]>;
}

function getApiErrorPayload(error: any): ApiErrorPayload {
  if (!error) return {};

  // Axios error: the backend body is under `response.data`.
  if (error.response?.data) {
    const data = error.response.data;
    return { message: data.message, errors: data.errors };
  }

  // Already-unwrapped payload (e.g. from `rejectWithValue`), or a plain
  // object shaped like `{ message, errors }`.
  if (typeof error === 'object' && (error.message || error.errors)) {
    return { message: error.message, errors: error.errors };
  }

  if (typeof error === 'string') {
    return { message: error };
  }

  return { message: error.message };
}

/**
 * A single line for a toast/alert. Prefers the first field-level validation
 * message ("The email has already been taken.") over the generic top-level
 * one ("Validation failed") since the field message is what actually tells
 * the user what to fix.
 */
export function getApiErrorMessage(error: any, fallback: string): string {
  const { message, errors } = getApiErrorPayload(error);

  if (errors) {
    for (const field of Object.keys(errors)) {
      const firstMessage = errors[field]?.[0];
      if (firstMessage) return firstMessage;
    }
  }

  return message || fallback;
}

/**
 * Field name -> first validation message, for highlighting the offending
 * field(s) in a form (e.g. `formErrors.email = "The email has already been
 * taken."`). Returns `{}` when the error carries no field-level detail.
 */
export function getApiFieldErrors(error: any): Record<string, string> {
  const { errors } = getApiErrorPayload(error);
  if (!errors) return {};

  const fieldErrors: Record<string, string> = {};
  for (const [field, messages] of Object.entries(errors)) {
    if (messages?.[0]) fieldErrors[field] = messages[0];
  }
  return fieldErrors;
}
